import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star, MessageSquareQuote, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = { name: "", city: "", rating: 5, text: "", active: true, product_id: "", photo: "", featured: false };

const Stars = ({ rating }) => (
  <span className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i < rating ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
    ))}
  </span>
);

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [products, setProducts] = useState([]);

  const load = () => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => {
    api.get("/products?limit=200").then((r) => setProducts(r.data.items || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, photo: data.url }));
      toast.success("Foto enviada!");
    } catch (err) {
      toast.error(formatApiError(err, "Falha no upload."));
    }
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, city: r.city || "", rating: r.rating, text: r.text, active: r.active, product_id: r.product_id || "", photo: r.photo || "", featured: !!r.featured });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      return toast.error("Informe o nome do cliente e o depoimento.");
    }
    setSaving(true);
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (editing) {
        await api.put(`/admin/reviews/${editing.id}`, payload);
        toast.success("Avaliação atualizada!");
      } else {
        await api.post("/admin/reviews", payload);
        toast.success("Avaliação cadastrada! Ela já aparece na página inicial.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/reviews/${deleting.id}`);
      toast.success("Avaliação excluída.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Avaliações de Clientes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cadastre apenas avaliações reais, autorizadas pelos clientes — as da loja aparecem na página inicial;
            as vinculadas a um produto aparecem na página dele e podem gerar estrelas no Google
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-full" data-testid="review-create-button">
          <Plus className="w-4 h-4" /> Nova Avaliação
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="p-4">Cliente</th>
              <th className="p-4">Nota</th>
              <th className="p-4">Depoimento</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">Carregando...</td></tr>
            ) : reviews.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-12 text-center text-slate-400">
                  <MessageSquareQuote className="w-10 h-10 mx-auto text-slate-200" />
                  <p className="mt-2">Nenhuma avaliação cadastrada ainda.</p>
                  <p className="text-xs mt-1">
                    Enquanto isso, a seção de avaliações fica oculta na loja — nada fictício é exibido.
                  </p>
                </td>
              </tr>
            ) : (
              reviews.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`admin-review-row-${r.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {r.photo ? (
                        <img src={r.photo} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                      ) : null}
                      <div>
                        <p className="font-medium text-gray-800">{r.name}</p>
                        {r.city && <p className="text-xs text-slate-400">{r.city}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4"><Stars rating={r.rating} /></td>
                  <td className="p-4 text-slate-600 max-w-[280px] truncate">{r.text}</td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${r.active ? "bg-green-50 text-green-700" : r.source === "site" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                        {r.active ? "Visível" : r.source === "site" ? "Pendente (aprovar)" : "Oculta"}
                      </span>
                      {r.featured && (
                        <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-yellow-50 text-yellow-700">Destaque</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`review-edit-${r.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleting(r)} data-testid={`review-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="review-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Avaliação" : "Nova Avaliação"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome do cliente *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Mariana S." className="mt-1.5" required data-testid="review-form-name" />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ex: Mesquita" className="mt-1.5" data-testid="review-form-city" />
              </div>
            </div>
            <div>
              <Label>Nota</Label>
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
                <SelectTrigger className="mt-1.5" data-testid="review-form-rating">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} estrela{n > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vincular a um produto (opcional)</Label>
              <Select value={form.product_id || "loja"} onValueChange={(v) => setForm({ ...form, product_id: v === "loja" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="review-form-product">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="loja">Avaliação da loja (página inicial)</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">
                Avaliações de produto aparecem na página do produto e alimentam as estrelas do Google.
              </p>
            </div>
            <div>
              <Label>Depoimento *</Label>
              <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} placeholder="O que o cliente disse sobre a compra..." className="mt-1.5" required data-testid="review-form-text" />
            </div>
            <div>
              <Label>Foto do cliente/produto (opcional — só com autorização)</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} placeholder="URL da foto ou faça upload" className="h-9 text-sm" data-testid="review-form-photo" />
                <label className="shrink-0 inline-flex items-center gap-1.5 border border-slate-200 rounded-md px-3 text-sm text-slate-600 hover:border-brand hover:text-brand cursor-pointer transition-colors" data-testid="review-photo-upload">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="review-form-active" />
                Visível na loja
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="review-form-featured" />
                Destaque na página inicial
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="review-form-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir avaliação?</AlertDialogTitle>
            <AlertDialogDescription>
              A avaliação de "{deleting?.name}" será removida permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="review-delete-confirm">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
